import React, { useState, useEffect, useRef } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { Link } from "react-router-dom";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL, NODE_API_BASE_URL } from "../../config";
import axios from "axios";
import { toast } from "react-toastify";
import { encode } from "html-entities";
import Select from "react-select";
import { authHeaders,getSessionToken } from "../utils/session";

// Constants for better maintainability
const PRICING_PLAN_OPTIONS = [
  { value: "1", label: "Attorney" },
  { value: "0", label: "Corporates" },
  { value: "2", label: "Both" },
  { value: "3", label: "None" }
];

const IN_MENU_OPTIONS = [
  { value: 0, label: "Not Apply" },
  { value: 1, label: "Header" },
  { value: 2, label: "Footer" },
];

const TINYMCE_CONFIG = {
  plugins:
    "print preview paste importcss searchreplace autolink autosave save directionality code visualblocks visualchars image link media template codesample table charmap hr pagebreak nonbreaking anchor toc insertdatetime advlist lists wordcount imagetools textpattern noneditable help charmap quickbars emoticons",
  table_default_attributes: {
    border: "5",
  },
  valid_elements: '*[*]', // Allow all elements and attributes
};

const INITIAL_FORM_STATE = {
  parent_link: "",
  page_name: "",
  page_title: "",
  page_url: "",
  short_description: "",
  description: "",
  page_slider_description: "",
  page_image: "",
  in_menu: "",
  menu_order: "",
  pricing_for: "0",
  menu_icon: "",
  header_group: "",
  sub_description: "",
};

const AddContentPage = () => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [image, setImage] = useState();
  const [errors, setErrors] = useState({});
  const [urlValue, setUrlValue] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [managePageData, setManagePageData] = useState([]);
  const [pageError, setPageError] = useState("");
  const [token, setToken] = useState(getSessionToken());
  
  const navigate = useNavigate();
  const { id } = useParams();
  // console.log(formData, "formdata");

  // Destructure form data for easier access
  const {
    parent_link,
    page_name,
    page_title,
    page_url,
    short_description,
    description,
    page_slider_description,
    page_image,
    in_menu,
    menu_order,
    pricing_for,
    menu_icon,
    header_group,
    sub_description,
  } = formData;

  // Custom hooks for API calls
  const useApi = () => {
    const makeRequest = async (url, options = {}) => {
      try {
        const response = await axios({
          url: `${API_BASE_URL}${url}`,
          headers: {
            ...authHeaders(),
            ...options.headers,
          },
          ...options,
        });
        return response.data;
      } catch (error) {
        console.error('API Error:', error);
        throw error;
      }
    };

    return { makeRequest };
  };

  const { makeRequest } = useApi();

  useEffect(() => {
    initializeComponent();
  }, []);

  const initializeComponent = async () => {
    setToken(getSessionToken());
    await fetchPageData();
    if (id) {
      await fetchPrefilled(id);
    }
  };

  const fetchPageData = async () => {
    try {
      const response = await makeRequest('/get-page-and-content', {
        headers: { 
          "content-type": "multipart/form-data",
          ...authHeaders(),


         },
      });
      setManagePageData(response.data?.reverse() || []);
    } catch (error) {
      console.error('Error fetching page data:', error);
    }
  };

  const fetchPrefilled = async (id) => {
    try {
      const response = await makeRequest(`/get-manage-content-details?id=${id}`);
      
      if (response.status === 200) {
        setFormData({
          ...INITIAL_FORM_STATE,
          ...response.data,
          page_image: "", // Reset file input
        });
        setImagePreview(response.data.page_image);
      }
    } catch (error) {
      console.error('Error fetching prefilled data:', error);
    }
  };

  // Validation function
  const validateForm = () => {
    const errors = {};
    
    if (!formData.page_name.trim()) {
      errors.page_name = "Please enter Page Name";
    }
    
    // Add more validation rules as needed
    return errors;
  };

  // Generic input change handler
  const handleInputChange = (event) => {
    const { name, value, type, files } = event.target;
    
    setErrors(prevErrors => ({ ...prevErrors, [name]: "" }));

    if (type === "file") {
      handleFileChange(event);
      return;
    }

    let updatedFormData = { ...formData, [name]: value };

    // Special handling for page_name to generate URL
    if (name === "page_name") {
      const url = value
        .toLowerCase()
        .replace(/[^a-zA-Z0-9 ]/g, "")
        .replace(/\s+/g, "-");
      updatedFormData.page_url = url;
      setUrlValue(url);
      setErrors(prevErrors => ({ ...prevErrors, page_url: "" }));
    }

    setFormData(updatedFormData);
  };

  // Handle file input changes
  const handleFileChange = (event) => {
    const selectedImage = event.target.files[0];
    setImage(selectedImage);
    
    if (!selectedImage) {
      setImagePreview(null);
      return;
    }
    
    const objectUrl = URL.createObjectURL(selectedImage);
    setImagePreview(objectUrl);
  };

  // Handle radio button changes
  const handleRadioChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
    setErrors(prevErrors => ({ ...prevErrors, [name]: "" }));
  };

  // Handle select dropdown changes
  const handleSelect = (fieldName, selectedOption) => {
    setFormData({ ...formData, [fieldName]: selectedOption.value });
    setErrors(prevErrors => ({ ...prevErrors, [fieldName]: "" }));
  };

  // Handle TinyMCE editor changes
  const handleEditorChange = (field) => (content) => {
    setFormData({ ...formData, [field]: content });
  };

  // Generate select options for parent link
  const generateParentLinkOptions = () => {
    const options = [{ value: "null", label: "Select" }];
    managePageData.forEach((item) => {
      options.push({ value: item.id, label: item.page_name });
    });
    return options;
  };

  // Custom select styles
  const getSelectStyles = (errorFieldName) => ({
    control: (provided) => ({
      ...provided,
      borderColor: errors[errorFieldName] ? "#dc3545" : provided.borderColor,
      boxShadow: errors[errorFieldName]
        ? "0 0 0 0.2rem rgba(220, 53, 69, 0.25)"
        : provided.boxShadow,
      minHeight: 22,
      height: 33,
    }),
  });

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const formDataToSend = new FormData();
    
    // Append all form fields
    Object.entries(formData).forEach(([key, value]) => {
      if (key !== 'page_image') {
        formDataToSend.append(key, value);
      }
    });
    
    if (image) {
      formDataToSend.append("page_image", image);
    }

    // Add additional fields for update
    if (id) {
      formDataToSend.append("id", id);
      formDataToSend.append("status", "update");
    }

    try {
      const response = await axios.post(
        `${NODE_API_BASE_URL}/api/add-page-and-content`,
        formDataToSend,
        { headers: { "Content-Type": "multipart/form-data", ...authHeaders() } },
      );

      if (response.status === 200) {
        navigate("/cms/managecontentpage");
        toast.success(id ? "Updated successfully" : "Added successfully");
      }
    } catch (error) {
      setPageError(error.response?.data?.result || "An error occurred");
    }
  };

  // Render form field wrapper
  const renderFormField = (label, children, required = false) => (
    <div className="form-group row mb-4">
      <label className="col-form-label col-lg-2">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      <div className="col-lg-10">
        {children}
      </div>
    </div>
  );

  // Render radio group
  const renderRadioGroup = (name, options, selectedValue) => (
    <div className="d-flex gap-4">
      {options.map((option) => (
        <div key={option.value} className="form-check">
          <input
            className="form-check-input"
            type="radio"
            name={name}
            id={`${name}_${option.value}`}
            value={option.value}
            checked={selectedValue == option.value}
            onChange={handleRadioChange}
          />
          <label className="form-check-label" htmlFor={`${name}_${option.value}`}>
            {option.label}
          </label>
        </div>
      ))}
    </div>
  );

  const parentLinkOptions = generateParentLinkOptions();

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 font-size-18">
                  {id ? 'Edit' : 'Add'} Content Page
                </h4>
                <div className="page-title-right">
                  <ol className="breadcrumb m-0">
                    <li className="breadcrumb-item">
                      <a href="javascript: void(0);">CMS</a>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/cms/managecontentpage">Manage Content Page</Link>
                    </li>
                    <li className="breadcrumb-item active">
                      {id ? 'Edit' : 'Add'} Content Page
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-12">
            <div className="row">
              <div className="col-lg-12">
                <div className="card">
                  <div className="card-body">
                    <h4 className="card-title mb-4">
                      {id ? 'Edit' : 'Add'} Content Page
                    </h4>
                    
                    <form onSubmit={handleSubmit}>
                      {/* In Menu */}
                      {renderFormField("In Menu", 
                        <Select
                          options={IN_MENU_OPTIONS}
                          value={IN_MENU_OPTIONS.find(option => option.value == in_menu)}
                          styles={getSelectStyles("in_menu")}
                          onChange={(selectedOption) => handleSelect("in_menu", selectedOption)}
                          placeholder="Select an option"
                        />
                      )}

                      {/* Parent Link */}
                      {renderFormField("Parent Link", 
                        <Select
                          options={parentLinkOptions}
                          value={parentLinkOptions.find(option => option.value == parent_link)}
                          styles={getSelectStyles("parent_link")}
                          onChange={(selectedOption) => handleSelect("parent_link", selectedOption)}
                          placeholder="Select an option"
                        />
                      )}

                      {/* Page Name */}
                      {renderFormField("Page Name", 
                        <>
                          <input
                            name="page_name"
                            type="text"
                            value={page_name}
                            className={errors.page_name ? "form-control is-invalid" : "form-control"}
                            placeholder="Enter Page Name..."
                            onChange={handleInputChange}
                          />
                          {errors.page_name && (
                            <div className="text-danger">{errors.page_name}</div>
                          )}
                        </>,
                        true
                      )}

                      {/* Page Title */}
                      {renderFormField("Page Title", 
                        <input
                          name="page_title"
                          type="text"
                          value={page_title}
                          className="form-control"
                          placeholder="Enter Page Title..."
                          onChange={handleInputChange}
                        />
                      )}

                      {/* Page URL */}
                      {renderFormField("Page URL", 
                        <>
                          <input
                            name="page_url"
                            type="text"
                            value={page_url}
                            className={errors.page_url ? "form-control is-invalid" : "form-control"}
                            placeholder="Enter Page URL..."
                            onChange={handleInputChange}
                          />
                          {errors.page_url && (
                            <div className="text-danger">{errors.page_url}</div>
                          )}
                        </>
                      )}

                      {/* Menu Order */}
                      {renderFormField("Menu Order", 
                        <input
                          name="menu_order"
                          type="number"
                          value={menu_order}
                          className="form-control"
                          placeholder="Enter Menu Order..."
                          onChange={handleInputChange}
                        />
                      )}

                      {/* Header Group */}
                      {renderFormField("Header Group",
                        <input
                          name="header_group"
                          type="text"
                          value={header_group}
                          className="form-control"
                          placeholder="e.g. Core Platform, Workflow & Automation, Business & Intelligence"
                          onChange={handleInputChange}
                        />
                      )}

                      {/* Menu Icon */}
                      {renderFormField("Menu Icon",
                        <input
                          name="menu_icon"
                          type="text"
                          value={menu_icon}
                          className="form-control"
                          placeholder="e.g. bx bx-briefcase or fa fa-search"
                          onChange={handleInputChange}
                        />
                      )}

                      {/* Sub Description */}
                      {renderFormField("Sub Description",
                        <input
                          name="sub_description"
                          type="text"
                          value={sub_description}
                          className="form-control"
                          placeholder="Short tagline shown under the module name"
                          onChange={handleInputChange}
                        />
                      )}

                      {/* Pricing Plan For - Radio Buttons */}
                      {renderFormField("Pricing Plan For",
                        renderRadioGroup("pricing_for", PRICING_PLAN_OPTIONS, pricing_for)
                      )}

                      {/* Short Description */}
                      {renderFormField("Short Description", 
                        <Editor
                          onEditorChange={handleEditorChange("short_description")}
                          value={short_description}
                          init={TINYMCE_CONFIG}
                        />
                      )}

                      {/* Description */}
                      {renderFormField("Description", 
                        <Editor
                          onEditorChange={handleEditorChange("description")}
                          value={description}
                          init={TINYMCE_CONFIG}
                        />
                      )}

                      {/* Page Slider Description */}
                      {renderFormField("Page Slider Description", 
                        <Editor
                          onEditorChange={handleEditorChange("page_slider_description")}
                          value={page_slider_description}
                          init={TINYMCE_CONFIG}
                        />
                      )}

                      {/* Image Upload */}
                      {renderFormField("Image", 
                        <>
                          <input
                            name="page_image"
                            type="file"
                            accept="image/*"
                            className="form-control"
                            onChange={handleInputChange}
                          />
                          {imagePreview && (
                            <div className="mt-3">
                              <img
                                src={imagePreview}
                                alt="Preview"
                                className="img-thumbnail"
                                style={{ width: "200px", height: "200px", objectFit: "cover" }}
                              />
                            </div>
                          )}
                        </>
                      )}

                      {/* Submit Button */}
                      <div className="row justify-content-end">
                        <div className="col-lg-10">
                          {pageError && (
                            <div className="alert alert-danger" role="alert">
                              {pageError}
                            </div>
                          )}
                          <button type="submit" className="btn btn-primary">
                            {id ? 'Update' : 'Add'} Content Page
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddContentPage;